// src/api.js
const API_URL = process.env.REACT_APP_API_URL || '/api';

let _401handler = null;

export function set401Handler(fn) {
  _401handler = fn;
}

async function fetchWithAuth(url, options = {}) {
  const res = await fetch(url, { ...options, credentials: 'include' });
  if (res.status === 401 && _401handler) {
    _401handler();
    throw { status: 401 };
  }
  return res;
}

export const api = {
  async getCases() {
    const response = await fetchWithAuth(`${API_URL}/cases`);
    if (!response.ok) throw new Error('Failed to fetch cases');
    return response.json();
  },

  async getCase(id) {
    const response = await fetchWithAuth(`${API_URL}/cases/${id}`);
    if (!response.ok) throw new Error('Failed to fetch case');
    return response.json();
  },

  async saveCase(caseData) {
    const response = await fetchWithAuth(`${API_URL}/cases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(caseData)
    });
    if (!response.ok) throw new Error('Failed to save case');
    return response.json();
  },

  async deleteCase(id) {
    const response = await fetchWithAuth(`${API_URL}/cases/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete case');
    return response.json();
  },

  async getPickingCards() {
    const response = await fetchWithAuth(`${API_URL}/picking-cards`);
    if (!response.ok) throw new Error('Failed to fetch picking cards');
    return response.json();
  },

  async getPickingCard(id) {
    const response = await fetchWithAuth(`${API_URL}/picking-cards/${id}`);
    if (!response.ok) throw new Error('Failed to fetch picking card');
    return response.json();
  },

  async savePickingCard(cardData) {
    const response = await fetchWithAuth(`${API_URL}/picking-cards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cardData)
    });
    if (!response.ok) throw new Error('Failed to save picking card');
    return response.json();
  },

  async deletePickingCard(id) {
    const response = await fetchWithAuth(`${API_URL}/picking-cards/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete picking card');
    return response.json();
  },

  async getImageLibrary(category = null) {
    const url = category && category !== 'All'
      ? `${API_URL}/image-library?category=${encodeURIComponent(category)}`
      : `${API_URL}/image-library`;
    const response = await fetchWithAuth(url);
    if (!response.ok) throw new Error('Failed to fetch image library');
    return response.json();
  },

  async getImageCategories() {
    const response = await fetchWithAuth(`${API_URL}/image-library/categories`);
    if (!response.ok) throw new Error('Failed to fetch categories');
    return response.json();
  },

  async uploadToLibrary(imageData) {
    const response = await fetchWithAuth(`${API_URL}/image-library`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(imageData)
    });
    if (!response.ok) throw new Error('Failed to upload image');
    return response.json();
  },

  async updateLibraryImage(id, data) {
    const response = await fetchWithAuth(`${API_URL}/image-library/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update image');
    return response.json();
  },

  async deleteLibraryImage(id) {
    const response = await fetchWithAuth(`${API_URL}/image-library/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete image');
    return response.json();
  },

  async getLibraryImageSrc(id) {
    const response = await fetchWithAuth(`${API_URL}/image-library/${id}/src`);
    if (!response.ok) throw new Error('Failed to fetch image src');
    return response.json();
  },

  async getMe() {
    const res = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
    if (!res.ok) throw { status: res.status };
    return res.json();
  },

  async login(username, password) {
    const response = await fetchWithAuth(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Login failed');
    }
    return response.json();
  },

  async logout() {
    await fetch(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
    // Ignore errors — if already logged out, that's fine
  },

  async getUsers() {
    const response = await fetchWithAuth(`${API_URL}/users`);
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
  },

  async createUser(data) {
    const response = await fetchWithAuth(`${API_URL}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to create user');
    }
    return response.json();
  },

  async updateUser(id, data) {
    const response = await fetchWithAuth(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to update user');
    }
    return response.json();
  },

  async deleteUser(id) {
    const response = await fetchWithAuth(`${API_URL}/users/${id}`, { method: 'DELETE' });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to delete user');
    }
    return response.json();
  }
};
